import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  ApiError,
  bootstrapAdmin,
  fetchSession,
  onUnauthorized,
  signInSuperAdmin,
  signOutServer,
} from "../api/client";
import { clearToken, getToken, setToken } from "../api/authToken";
import {
  configuredIdp,
  getSsoToken,
  sessionIsCookieBased,
  signIn,
  signOut,
} from "../api/sso";
import type { SessionInfo } from "../api/types";

export interface AuthContextValue {
  /** Whether a credential is held, from either sign-in route. */
  hasToken: boolean;
  /** Who the caller is and whether they administer this deployment, once known. */
  session: SessionInfo | null;
  /** True while the initial or a refreshed session check is in flight. */
  loadingSession: boolean;
  /** Set when the session check itself failed for a reason other than 401 (which logs out). */
  sessionError: Error | null;
  /** Hand off to the organisation's identity provider, then check who came back. */
  loginWithSso: () => Promise<void>;
  /** Sign in with the local break-glass account. Throws `ApiError` on refusal. */
  loginWithPassword: (username: string, password: string) => Promise<void>;
  /** Discard the token and return to the login screen. */
  logout: () => void;
  /** Re-run the session check, e.g. after a successful bootstrap. */
  refreshSession: () => Promise<void>;
  /** Which identity provider this build signs in against, or "" when SSO is not configured. */
  ssoProvider: string;
  /**
   * Attempt to claim this deployment as its first administrator. Throws `ApiError` on refusal
   * (wrong password, or the deployment already has an admin — the backend deliberately reports
   * both with the same vague message) so the login screen can show it inline.
   */
  bootstrap: (password: string) => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components -- context object, not a component
export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [hasToken, setHasToken] = useState(() => getToken() !== null);
  const ssoProvider = configuredIdp();
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [loadingSession, setLoadingSession] = useState(false);
  const [sessionError, setSessionError] = useState<Error | null>(null);

  const logout = useCallback(() => {
    clearToken();
    void signOut();
    // The Google flow's session is a backend cookie, which only the backend can end.
    void signOutServer();
    setHasToken(false);
    setSession(null);
    setSessionError(null);
  }, []);

  const refreshSession = useCallback(async () => {
    setLoadingSession(true);
    setSessionError(null);
    try {
      const result = await fetchSession();
      setSession(result);
      // A successful session check *is* the credential check for the cookie flow, where no
      // token is ever held client-side.
      setHasToken(true);
    } catch (error) {
      // A 401 already triggered `logout` via the `onUnauthorized` subscription below; anything
      // else (network failure, 5xx) is a session-check error to show, not a reason to sign out.
      if (!(error instanceof ApiError && error.status === 401)) {
        setSessionError(error instanceof Error ? error : new Error(String(error)));
      }
      setSession(null);
    } finally {
      setLoadingSession(false);
    }
  }, []);

  const loginWithSso = useCallback(async () => {
    // The token is not stored: `getAccessToken` asks the provider for it on every request, so it
    // is renewed silently and never outlives the provider's own session.
    await signIn();
    setHasToken(true);
    await refreshSession();
  }, [refreshSession]);

  const loginWithPassword = useCallback(
    async (username: string, password: string) => {
      const session = await signInSuperAdmin({ username, password });
      setToken(session.token);
      setHasToken(true);
      await refreshSession();
    },
    [refreshSession],
  );

  const bootstrap = useCallback(
    async (password: string) => {
      await bootstrapAdmin({ password });
      await refreshSession();
    },
    [refreshSession],
  );

  useEffect(() => {
    onUnauthorized(logout);
    return () => onUnauthorized(null);
  }, [logout]);

  useEffect(() => {
    // On mount, pick up any credential a page reload (or the backend sign-in redirect) may have
    // left behind: a break-glass token in sessionStorage, a provider session the SSO library can
    // renew silently, or — for the cookie flow — a backend session only `/api/session` can see.
    void (async () => {
      if (getToken() !== null || (await getSsoToken()) !== null || sessionIsCookieBased()) {
        setHasToken(true);
        await refreshSession();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      hasToken,
      session,
      loadingSession,
      sessionError,
      loginWithSso,
      loginWithPassword,
      logout,
      refreshSession,
      bootstrap,
      ssoProvider,
    }),
    [
      hasToken,
      session,
      loadingSession,
      sessionError,
      loginWithSso,
      loginWithPassword,
      logout,
      refreshSession,
      bootstrap,
      ssoProvider,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
